import { Box, TextField, Typography, IconButton } from '@material-ui/core';
import React, { useState } from 'react';
import { getListForSqlIn } from '../helpers/functions';
import { makeStyles } from '@material-ui/core/styles';
import ClearAllRoundedIcon from '@material-ui/icons/ClearAllRounded';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles((theme) => ({
    filterInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        // --- LARGEUR FIXE DU COMPOSANT ---
        flexShrink: 0, // Empêche le composant de rétrécir si l'espace est limité
        flexGrow: 0,   // Empêche le composant de s'agrandir si l'espace est disponible
    },
    // --- LIGNE POUR TEXTFIELD + ICÔNES ---
    inputRow: {
        display: 'flex',
        alignItems: 'center', // Alignement vertical au centre
        gap: theme.spacing(0.5), // Petit espacement entre le TextField et les icônes
        width: '100%', // Prend toute la largeur du parent (filterInputContainer)
        marginBottom: theme.spacing(0.5), // Espace sous cette ligne
    },
    textFieldWrapper: {
        flexGrow: 1, // Permet au TextField de prendre l'espace restant
        minWidth: 0, // Crucial pour éviter le débordement dans les flex containers
        // Gérer le débordement du texte dans le TextField
        '& .MuiOutlinedInput-root': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: theme.spacing(1), // Réduit le padding pour laisser de la place au texte si besoin
        },
        // Gérer le débordement pour le label flottant
        '& .MuiInputLabel-outlined': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 24px)', // Ajuste la largeur max du label pour éviter le chevauchement
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75) !important', // Assure la stabilité du label
        },
    },
    actionIconsContainer: {
        display: 'flex',
        flexShrink: 0, // Empêche ce conteneur d'icônes de rétrécir
    },
    // --- LISTE DES ÉLÉMENTS SÉLECTIONNÉS ---
    selectedItemsBox: {
        // Updated maxHeight to show one more line
        maxHeight: 110, // Increased from 90 to 110 (assuming ~20px per line for body2 typography)
        overflowY: 'auto', // Active le défilement vertical si besoin
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.5),
        backgroundColor: theme.palette.grey[50],
        width: '100%',
        boxSizing: 'border-box', // Inclut padding et border dans la largeur totale
        // Styles pour la barre de défilement pour une meilleure apparence
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.grey[100],
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.grey[600],
        },
    },
    selectedItemText: {
        cursor: 'pointer',
        padding: theme.spacing(0.25, 0.5),
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between', // Pour espacer le texte et l'icône de suppression
        // Gérer le débordement du texte pour chaque élément sélectionné
        whiteSpace: 'nowrap', // Le texte reste sur une seule ligne
        overflow: 'hidden',    // Cache tout ce qui dépasse
        textOverflow: 'ellipsis', // Ajoute des points de suspension
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            textDecoration: 'none',
        },
    },
    noSelectionText: {
        padding: theme.spacing(1),
    }
}));

const FilterInput = ({ label = 'Label', onChange, type = 'text', ...props }) => {
    const classes = useStyles();
    const [current, setCurrent] = useState('');
    const [selected, setSelected] = useState([]);

    const handleChange = (e) => {
        setCurrent(e.target.value);
    };

    const handleRemoveItem = id => {
        const newSelected = selected.filter(item => item !== id);
        setSelected(newSelected);
        onChange(getListForSqlIn(newSelected, ''), label);
    };

    const onEnter = e => {
        if (e.key === 'Enter' && current.trim() !== '') {
            const exist = selected.find(r => r === current.trim());
            if (exist === undefined) {
                const newSelected = selected.concat(current.trim());
                setSelected(newSelected);
                onChange(getListForSqlIn(newSelected), label);
                setCurrent('');
            }
        }
    };

    const reset = () => {
        setSelected([]);
        onChange('', label);
        setCurrent('');
    };

    return (
        <Box className={classes.filterInputContainer} {...props}>
            {/* Ligne contenant le TextField et les icônes */}
            <Box className={classes.inputRow}>
                <div className={classes.textFieldWrapper}>
                    <TextField
                        fullWidth
                        type={type}
                        onKeyPress={onEnter}
                        onChange={handleChange}
                        value={current}
                        label={label}
                        variant="outlined"
                        margin="dense"
                        // Force le label à se réduire si le champ a une valeur ou si la liste est vide mais non focusee
                        InputLabelProps={{ shrink: current !== '' || selected.length > 0 || undefined }}
                    />
                </div>
                {/* Icône d'action pour effacer la sélection */}
                <Box className={classes.actionIconsContainer}>
                    {selected.length !== 0 && (
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={reset}
                            aria-label="Effacer la sélection"
                            title="Effacer la sélection"
                        >
                            <ClearAllRoundedIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Conteneur de la liste des éléments sélectionnés */}
            <Box className={classes.selectedItemsBox}>
                {selected.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" align="center" className={classes.noSelectionText}>
                        Aucune sélection
                    </Typography>
                ) : (
                    selected.map((item) => (
                        <Typography
                            variant="body2"
                            align="left"
                            key={item}
                            className={classes.selectedItemText}
                        >
                            {item}
                            <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(item)}
                                aria-label={`Supprimer ${item}`}
                                title={`Supprimer ${item}`}
                                style={{ padding: 2 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Typography>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default FilterInput;